import {
  Button,
  Card,
  Grid,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { organizationApi } from 'src/api/organization';
import { organizationAvailabilityApi } from 'src/api/organization-availability';
import { useUserContext } from 'src/auth/useUserContext';
import CalendarSchedule, {
  ExtendedAvailabilitySlotDto,
} from 'src/components/calendar/calendar-schedule';
import CalendarScheduleSkeleton from 'src/components/calendar/calendar-schedule-skeleton';
import Iconify from 'src/components/iconify';
import { translateKey } from 'src/locales/translate';
import ScheduleConflictWarningModal from 'src/sections/@dashboard/organization/components/ScheduleConflictWarningModal';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import TimeOffModal from 'src/sections/@dashboard/visit/components/TimeOffModal';
import { AvailabilitySlotDto, ConflictingVisitDto, UpdateAvailabilityDto } from 'woofwoof-api';


export default function OrganizationDetailsProfessionalScheduleTabsPage() {
  const theme = useTheme();
  const { organizationId } = useParams();
  const { isProfessionalRoleSelected, organizations } =
    useOrganizationContext();
  const { data: user } = useUserContext();
  const [isTimeOffModalOpen, setIsTimeOffModalOpen] = useState(false);
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [conflictingVisits, setConflictingVisits] = useState<ConflictingVisitDto[]>([]);
  const [isConfirming, setIsConfirming] = useState(false);
  const pendingChangeRef = useRef<{
    apply: () => Promise<AxiosResponse<any>>;
    resolve: (value: any) => void;
    reject: (reason?: any) => void;
  } | null>(null);

  // Organization schedule data (for organization admins)
  const { data: organizationData, isLoading: isOrganizationLoading } = useQuery(
    ['organization', organizationId],
    () =>
      organizationApi.organizationControllerGetOrganizationById({
        id: organizationId!,
      }),
    {
      retry: 2,
      enabled: !!organizationId,
    },
  );

  // Professional time off data (for professionals)
  const {
    data: organizationTimeOffData,
    isLoading: isTimeOffLoading,
    refetch: refetchTimeOff,
  } = useQuery({
    queryKey: ['time-off-in-organization', organizationId, user?.id],
    queryFn: async () => {
      const result =
        await organizationAvailabilityApi.organizationAvailabilityControllerGetOrganizationTimeOff(
          {
            professionalId: user?.id!,
            organizationId: organizationId!,
          },
        );
      return result;
    },
    enabled: !!organizationId && !!user?.id && isProfessionalRoleSelected,
    onError: (err) => {
      console.error('Error fetching time off:', err);
    },
  });

  // Professional availability data (for professionals)
  const {
    data: organizationAvailabilityData,
    isLoading: isProfessionalLoading,
    refetch: refetchProfessional,
  } = useQuery({
    queryKey: ['all-organization-availabilities', organizationId, user?.id],
    queryFn: async () => {
      const result =
        await organizationAvailabilityApi.organizationAvailabilityControllerGetProfessionalOrganizationAvailabilities(
          {
            professionalId: user?.id!,
          },
        );
      return result;
    },
    enabled: !!organizationId && !!user?.id && isProfessionalRoleSelected,
    onError: (err) => {
      console.error('Error fetching availability:', err);
    },
  });

  // Professional availability mutation (for professionals)
  const {
    mutateAsync: mutateAsyncProfessionalAvailability,
    isLoading: isProfessionalAvailabilityLoading,
  } = useMutation({
    mutationFn: ({
      availabilitySlots,
      dryRun,
    }: {
      availabilitySlots: AvailabilitySlotDto[];
      dryRun?: boolean;
    }) =>
      organizationAvailabilityApi.organizationAvailabilityControllerSetOrganizationAvailability(
        {
          organizationId: organizationId!,
          professionalId: user?.id!,
          setOrganizationAvailabilityDto: {
            availabilitySlots,
            dryRun,
          } as UpdateAvailabilityDto,
        },
      ),
  });

  // Professional time off mutation (for professionals). Uses the availability
  // endpoint (dryRun-capable, returns conflictingVisits) — the same one the
  // calendar uses — replacing the deprecated user time-off endpoint so the
  // conflict-preview flow applies here too.
  const {
    mutateAsync: mutateAsyncProfessionalTimeOff,
    isLoading: isProfessionalTimeOffLoading,
  } = useMutation({
    mutationFn: ({ timeOff, dryRun }: { timeOff: any[]; dryRun?: boolean }) =>
      organizationAvailabilityApi.organizationAvailabilityControllerSetOrganizationTimeOff(
        {
          organizationId: organizationId!,
          professionalId: user?.id!,
          setOrganizationTimeOffDto: { timeOff: timeOff as any, dryRun },
        },
      ),
  });

  // Process and flatten the organization availability data
  const processedAvailabilitySlots = useMemo(() => {
    if (
      !organizationAvailabilityData?.data ||
      !organizations ||
      !organizationId
    ) {
      return [];
    }

    const flattenedSlots: ExtendedAvailabilitySlotDto[] = [];

    // organizationAvailabilityData.data is an array of organization availability objects
    const availabilityArray = Array.isArray(organizationAvailabilityData.data)
      ? organizationAvailabilityData.data
      : [organizationAvailabilityData.data];

    availabilityArray.forEach((orgAvailability: any) => {
      const { organizationId: slotOrgId, availabilitySlots } = orgAvailability;

      availabilitySlots.forEach((slot: any) => {
        const processedSlot = { ...slot };

        // Add organizationName if the slot is from a different organization
        if (slotOrgId !== organizationId) {
          const organization = organizations.find(
            (org) => org.id === slotOrgId,
          );
          if (organization) {
            (processedSlot as any).organizationName = organization.name;
          }
        }

        flattenedSlots.push(processedSlot);
      });
    });

    return flattenedSlots;
  }, [organizationAvailabilityData?.data, organizations, organizationId]);

  // Get current organization's working hours from selectedOrganization
  const currentOrganizationWorkingHours: AvailabilitySlotDto[] =
    (organizationData?.data?.workingHours as AvailabilitySlotDto[]) || [];

  // Check if data is fully processed and ready to render
  const isDataReady = useMemo(() => {
    if (
      !organizationAvailabilityData?.data ||
      !organizations ||
      !organizationId
    ) {
      return false;
    }

    // Ensure all slots from other organizations have organizationName property
    return processedAvailabilitySlots.every((slot) => {
      // Find which organization this slot belongs to
      const availabilityArray = Array.isArray(organizationAvailabilityData.data)
        ? organizationAvailabilityData.data
        : [organizationAvailabilityData.data];

      const slotOrg = availabilityArray.find((orgAvailability: any) =>
        orgAvailability.availabilitySlots.some(
          (s: any) =>
            s.start === slot.start &&
            s.end === slot.end &&
            s.weekday === slot.weekday,
        ),
      );

      if (!slotOrg) return true; // Slot not found, consider it ready

      // If slot is from a different organization, it should have organizationName
      if (slotOrg.organizationId !== organizationId) {
        return !!slot.organizationName;
      }

      return true; // Slot is from current organization, no organizationName needed
    });
  }, [
    processedAvailabilitySlots,
    organizationAvailabilityData?.data,
    organizations,
    organizationId,
  ]);

  // Dry-run preview → if conflicts, open the confirm modal and apply only on
  // Potwierdź; otherwise apply immediately. The returned promise resolves with
  // the apply response (success), or `{ cancelled: true }` if the user backs
  // out — callers skip their success handling in that case.
  const gatedSave = (
    apply: () => Promise<AxiosResponse<any>>,
    preview: () => Promise<AxiosResponse<any>>,
  ): Promise<any> =>
    new Promise((resolve, reject) => {
      preview()
        .then((pre) => {
          const conflicts = pre.data?.conflictingVisits ?? [];
          if (conflicts.length === 0) {
            apply().then(resolve, reject);
            return;
          }
          setConflictingVisits(conflicts);
          pendingChangeRef.current = { apply, resolve, reject };
          setConflictModalOpen(true);
        })
        .catch(reject);
    });

  const onSave = (workingHours: AvailabilitySlotDto[]): Promise<any> =>
    gatedSave(
      () =>
        mutateAsyncProfessionalAvailability({
          availabilitySlots: workingHours,
          dryRun: false,
        }),
      () =>
        mutateAsyncProfessionalAvailability({
          availabilitySlots: workingHours,
          dryRun: true,
        }),
    );

  const handleConfirmChange = async () => {
    const pending = pendingChangeRef.current;
    if (!pending) return;
    setIsConfirming(true);
    try {
      pending.resolve(await pending.apply());
    } catch (error) {
      pending.reject(error);
    } finally {
      setIsConfirming(false);
      setConflictModalOpen(false);
      pendingChangeRef.current = null;
    }
  };

  const handleCancelChange = () => {
    pendingChangeRef.current?.resolve({ cancelled: true });
    pendingChangeRef.current = null;
    setConflictModalOpen(false);
  };

  const onSaveTimeOff = (timeOffDto: Object[]): Promise<any> =>
    gatedSave(
      () =>
        mutateAsyncProfessionalTimeOff({ timeOff: timeOffDto, dryRun: false }),
      () =>
        mutateAsyncProfessionalTimeOff({ timeOff: timeOffDto, dryRun: true }),
    );

  const handleOpenTimeOffModal = () => {
    setIsTimeOffModalOpen(true);
  };

  const handleCloseTimeOffModal = () => {
    setIsTimeOffModalOpen(false);
  };

  if (!organizationId) {
    return null;
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12} lg={12} xl={12}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flex: 1,
            boxShadow: theme.customShadows.z24,
          }}
        >
          <Stack sx={{ height: 1, minHeight: '560px', flex: 1 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              p={3}
              pb={2}
            >
              <Typography variant="h6">{translateKey("visit.scheduleModalTitle")}</Typography>
              {isProfessionalLoading ? (
                <Skeleton variant="rounded" sx={{ width: 164, height: 36 }} />
              ) : (
                <Button
                  onClick={handleOpenTimeOffModal}
                  disabled={
                    isProfessionalAvailabilityLoading ||
                    isProfessionalLoading ||
                    isProfessionalTimeOffLoading ||
                    isOrganizationLoading
                  }
                  variant="contained"
                  sx={{
                    background: theme.palette.common.black,
                    color: theme.palette.common.white,
                  }}
                  startIcon={
                    <Iconify
                      width="20px"
                      height="20px"
                      icon="mdi:calendar-remove"
                    />
                  }
                >
                  Ustaw dni wolne
                </Button>
              )}
            </Stack>
            {isProfessionalLoading ||
            !organizationAvailabilityData ||
            !organizations ||
            !isDataReady ? (
              <CalendarScheduleSkeleton />
            ) : (
              <CalendarSchedule
                data={processedAvailabilitySlots}
                timeOff={organizationTimeOffData?.data || []}
                refetch={refetchProfessional}
                onSave={onSave}
                isLoading={
                  isProfessionalAvailabilityLoading ||
                  isProfessionalLoading ||
                  isProfessionalTimeOffLoading ||
                  isOrganizationLoading
                }
                organizationWorkingHours={currentOrganizationWorkingHours}
              />
            )}
          </Stack>
          <TimeOffModal
            isOpen={isTimeOffModalOpen}
            onClose={handleCloseTimeOffModal}
            timeOff={organizationTimeOffData?.data || []}
            refetch={refetchTimeOff}
            onSave={onSaveTimeOff}
            professionalId={user?.id || ''}
            professionalName={`${user?.firstName || ''} ${
              user?.lastName || ''
            }`}
          />
          <ScheduleConflictWarningModal
            open={conflictModalOpen}
            onClose={handleCancelChange}
            onConfirm={handleConfirmChange}
            isConfirming={isConfirming}
            conflicts={conflictingVisits}
          />
        </Card>
      </Grid>
    </Grid>
  );
}
